import { useMutation, useQuery } from '@apollo/client';
import {
  ContextualMenu,
  IDragOptions,
  makeStyles,
  Modal,
  Stack,
} from '@fluentui/react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DataAccessPolicyModalProps } from '..';
import { DataAccessPolicyRow } from '../../list';
import { DataAccessPolicyCommonData } from './__generated__/DataAccessPolicyCommonData';
import {
  DataAccessPolicyCreate,
  DataAccessPolicyCreateVariables,
} from './__generated__/DataAccessPolicyCreate';
import { BasicForm } from './BasicForm';
import { BusinessUnitAccessForm } from './BusinessUnitAccessForm';
import COMMON_DATA from './DataAccessPolicyCommonData.graphql';
import DATA_ACCESS_POLICY_CREATE from './DataAccessPolicyCreate.graphql';
import DATA_ACCESS_POLICY_UPDATE from './dataAccessPolicyUpdate.graphql';
import { Header } from './Header';
import { BusinessUnitAccessRowValues, DataAccessPolicyValues } from './types';
import { getDefaultValues } from './utils';

import { yupResolver } from '@hookform/resolvers/yup';
import { showCustomToast } from 'common/components/CustomToast/utils';
import {
  DataAccessBusinessUnitInput,
  DataAccessBusinessUnitUpdateTypeInput,
  DataAccessPolicyUpdateInput,
  EntityDeleteInput,
} from 'common/types/globalTypes';
import { DATA_ACCESS_POLICY_VALUES } from './constant';
import { Footer } from './Footer';
import { validationSchema } from './validation';

import { differenceBy, intersection, isEmpty } from 'lodash';
import DATA_ACCESS_POLICY_FIELDS from '../../list/DataAccessPolicyFields.graphql';
import {
  DataAccessPolicyUpdate,
  DataAccessPolicyUpdateVariables,
} from './__generated__/dataAccessPolicyUpdate';
import DELETE_DATA_ACCESS_POLICY from './DataAccessPolicyDelete.graphql';
import {
  DataAccessPolicyDelete,
  DataAccessPolicyDeleteVariables,
} from './__generated__/DataAccessPolicyDelete';

const DATA_ACCESS_POLICY_FRAGMENT_NAME = 'DataAccessPolicyFields';

type FormModalProps = DataAccessPolicyModalProps & {
  onClose: () => void;
};
const DragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
  dragHandleSelector: '.ms-Modal-scrollableContent > div > div:first-child ',
};
const useStyles = makeStyles(() => ({
  container: {
    width: 900,
    maxHeight: 820,
  },
}));

export const FormModal: React.FC<FormModalProps> = ({
  existingDataAccessPolicy,
  onClose,
}) => {
  const styles = useStyles();

  const formMethods = useForm<DataAccessPolicyValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });
  const { handleSubmit, reset } = { ...formMethods };
  const { id, _rowTimestamp, _isUpdatable, _isDeletable } = {
    ...existingDataAccessPolicy,
  };

  const { data: commonData } =
    useQuery<DataAccessPolicyCommonData>(COMMON_DATA);
  const { companyDepartmentGroups, companyBusinessUnits } = { ...commonData };

  const [deleteDataAccessPolicy] = useMutation<
    DataAccessPolicyDelete,
    DataAccessPolicyDeleteVariables
  >(DELETE_DATA_ACCESS_POLICY, { errorPolicy: 'all' });

  const _deleteDataAccessPolicy = async () => {
    const { errors } = await deleteDataAccessPolicy({
      variables: {
        input: {
          entityDelete: [
            {
              id: existingDataAccessPolicy?.id!,
              rowTimestamp: existingDataAccessPolicy?._rowTimestamp!,
            },
          ],
        },
      },
      update: (cache, { errors }) => {
        if (!errors?.length) {
          const identity = `DataAccessPolicy:${existingDataAccessPolicy?.id}`;
          cache.evict({ id: identity });
          cache.gc();
          onClose();
        }
      },
    });
    if (errors?.length)
      showCustomToast({
        content: `${errors[0].message}`,
        appearance: 'error',
      });
    else {
      showCustomToast({
        content: 'Data access policy deleted successfully',
        appearance: 'success',
      });
    }
  };

  useEffect(() => {
    if (!existingDataAccessPolicy) {
      reset(DATA_ACCESS_POLICY_VALUES);
    } else {
      const defaultValues = getDefaultValues({
        existingDataAccessPolicy,
      });
      reset(defaultValues);
    }
  }, [existingDataAccessPolicy, reset]);

  const [createDataAccessPolicy] = useMutation<
    DataAccessPolicyCreate,
    DataAccessPolicyCreateVariables
  >(DATA_ACCESS_POLICY_CREATE, { errorPolicy: 'all' });

  const [updateDataAccessPolicy] = useMutation<
    DataAccessPolicyUpdate,
    DataAccessPolicyUpdateVariables
  >(DATA_ACCESS_POLICY_UPDATE, { errorPolicy: 'all' });

  const onHandleSubmit = async (values: DataAccessPolicyValues) => {
    const { dataAccessBusinessUnits, name, ...formValues } = { ...values };
    if (existingDataAccessPolicy) {
      const defaultValues = getDefaultValues({
        existingDataAccessPolicy,
      });
      const newDataAccessBusinessUnits = (dataAccessBusinessUnits ?? [])
        .filter((item) => !item.id && item.businessUnitId !== null)
        .map(
          ({ id, _rowTimestamp, _isUpdatable, _isDeletable, ...rest }) =>
            rest as DataAccessBusinessUnitInput
        );

      const updatedBusinessUnitsAccess =
        defaultValues.dataAccessBusinessUnits &&
        dataAccessBusinessUnits &&
        intersection(
          defaultValues.dataAccessBusinessUnits.map((ele) => ele.id),
          dataAccessBusinessUnits.filter((ele) => ele.id).map((item) => item.id)
        ).reduce((arr, targetId) => {
          const initialDataAccessBusinessUnit =
            defaultValues.dataAccessBusinessUnits?.find(
              (ele) => ele.id === targetId
            )!;
          const { id, _rowTimestamp, ...updatedBusinessUnit } =
            dataAccessBusinessUnits?.find((unit) => unit.id === targetId)!;
          const patch = Object.entries(updatedBusinessUnit).reduce(
            (res, [key, val]) => {
              if (
                val !==
                initialDataAccessBusinessUnit[
                  key as keyof BusinessUnitAccessRowValues
                ]
              )
                return { ...res, [key]: val };
              return res;
            },
            {}
          );
          if (!isEmpty(patch))
            return [
              ...arr,
              {
                id,
                rowTimestamp: _rowTimestamp,
                dataAccessBusinessUnitPatch: patch,
              },
            ];
          return arr;
        }, [] as DataAccessBusinessUnitUpdateTypeInput[]);

      const deletedBusinessUnitAccess =
        defaultValues.dataAccessBusinessUnits &&
        (differenceBy(
          defaultValues.dataAccessBusinessUnits,
          dataAccessBusinessUnits || [],
          'id'
        )
          .filter((item) => !!item.id && !!item._rowTimestamp)
          .map((ele) => ({
            id: ele.id,
            rowTimestamp: ele._rowTimestamp,
          })) as EntityDeleteInput[]);
      const { errors } = await updateDataAccessPolicy({
        variables: {
          input: {
            id,
            rowTimestamp: _rowTimestamp,
            dataAccessPolicyPatch: {
              ...formValues,
              name: name,
            },

            dataAccessBusinessUnitsCreate: newDataAccessBusinessUnits,
            dataAccessBusinessUnitsDelete: deletedBusinessUnitAccess,
            dataAccessBusinessUnitsUpdate: updatedBusinessUnitsAccess,
          } as DataAccessPolicyUpdateInput,
        },
      });
      if (errors?.length)
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      else {
        showCustomToast({
          content: 'Data access policy updated successfully.',
          appearance: 'success',
        });
      }
    } else {
      const { dataAccessBusinessUnits, ...formValues } = { ...values };
      const { errors } = await createDataAccessPolicy({
        variables: {
          input: {
            dataAccessPolicy: {
              ...formValues,
              name: name ?? '',
            },

            dataAccessBusinessUnit:
              dataAccessBusinessUnits
                ?.filter((item) => item.businessUnitId !== null)
                .map(
                  ({
                    id,
                    _rowTimestamp,
                    _isUpdatable,
                    _isDeletable,
                    businessUnitId,
                    ...rest
                  }) => ({
                    ...rest,
                    businessUnitId: businessUnitId as string,
                  })
                ) ?? [],
          },
        },
        update: (cache, { data }) => {
          if (!!data?.dataAccessPolicyCreate?.dataAccessPolicy?.id) {
            cache.modify({
              fields: {
                dataAccessPolicies(existing) {
                  if (data?.dataAccessPolicyCreate?.dataAccessPolicy) {
                    const newDataPolicyRef = cache.writeFragment({
                      data: data.dataAccessPolicyCreate.dataAccessPolicy,
                      fragment: DATA_ACCESS_POLICY_FIELDS,
                      fragmentName: DATA_ACCESS_POLICY_FRAGMENT_NAME,
                    });
                    return {
                      ...existing,
                      nodes: [newDataPolicyRef, ...existing.nodes],
                    };
                  }
                },
              },
            });
          }
        },
      });
      if (errors?.length) {
        onClose();
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      } else {
        onClose();
        showCustomToast({
          content: 'Data access policy created successfully',
          appearance: 'success',
        });
      }
    }
  };

  return (
    <Modal isOpen isBlocking dragOptions={DragOptions}>
      <Stack className={styles.container}>
        <FormProvider {...formMethods}>
          <Header
            onClose={onClose}
            onDelete={_deleteDataAccessPolicy}
            isNew={!existingDataAccessPolicy}
            _isDeletable={!!_isDeletable}
          />
          <BasicForm
            companyDepartmentGroups={companyDepartmentGroups}
            _isUpdatable={!existingDataAccessPolicy || !!_isUpdatable}
          />
          <BusinessUnitAccessForm
            companyBusinessUnits={companyBusinessUnits}
            _isUpdatable={!existingDataAccessPolicy || !!_isUpdatable}
          />
          <Footer
            loading={false}
            onSubmit={handleSubmit(onHandleSubmit)}
            onCancel={onClose}
          />
        </FormProvider>
      </Stack>
    </Modal>
  );
};
